import { useState } from 'react';
import type { LandCategory, LandCategoryAllocation } from '@mtg-deck-tool/shared';
import { LAND_CATALOG } from '../../data/land-catalog.js';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Categories that are tapland exceptions — shown with a "(taps)" indicator. */
const TAPLAND_EXCEPTION_CATEGORIES = new Set<LandCategory>([
  'surveil',
  'bounce',
  'triome',
  'turbulent',
]);

/**
 * Display order for land type categories, sorted by priority/popularity in Commander.
 */
const DISPLAY_ORDER: LandCategory[] = [
  'rainbow',          // Handled separately as staples — but keep in order for fallback
  'fetch-on',
  'fetch-off',
  'shock',
  'marvel-dual',
  'surveil',
  'triome',
  'bond',
  'turbulent',
  'verge',
  'fast',
  'check',
  'bounce',
  'pain',
  // ─── "More" section below this line ───
  'og-dual',
  'filter',
  'slow',
  'reveal',
  'pathway',
  'horizon',
  'battle',
  'tainted',
];

/** Categories that appear in the primary section (above the "More" fold). */
const PRIMARY_CATEGORIES = new Set<LandCategory>([
  'fetch-on', 'fetch-off', 'shock', 'marvel-dual', 'surveil', 'triome',
  'bond', 'turbulent', 'verge', 'fast', 'check', 'bounce', 'pain',
]);

/** Build description and badge lookups from the catalog at module load time. */
const CYCLE_DESCRIPTIONS = new Map<LandCategory, string>(
  LAND_CATALOG
    .filter((c) => c.description !== undefined)
    .map((c) => [c.category, c.description!]),
);

const FETCHABLE_CATEGORIES = new Set<LandCategory>(
  LAND_CATALOG.filter((c) => c.isFetchable).map((c) => c.category),
);

const CYCLE_PRICES = new Map<LandCategory, number>(
  LAND_CATALOG.filter((c) => c.avgPriceUsd != null).map((c) => [c.category, c.avgPriceUsd!]),
);

// ─── Props ────────────────────────────────────────────────────────────────────

interface LandTypeSelectorProps {
  allocations: LandCategoryAllocation[];
  enabledCategories: LandCategory[];
  onToggle: (category: LandCategory) => void;
  categoryConstraints?: Map<LandCategory, { min?: number; max?: number }>;
  onSetMin?: (category: LandCategory, value: number | undefined) => void;
  onSetMax?: (category: LandCategory, value: number | undefined) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Land type category selector with checkboxes.
 *
 * Each enabled category is included in the simulation. The Monte Carlo
 * engine determines optimal allocation across enabled categories.
 */
export default function LandTypeSelector({
  allocations,
  enabledCategories,
  onToggle,
  categoryConstraints,
  onSetMin,
  onSetMax,
}: LandTypeSelectorProps) {
  const [showMore, setShowMore] = useState(false);

  // Non-basic allocations sorted by display priority — exclude rainbow (handled separately)
  const allNonBasic = allocations
    .filter((a) => a.category !== 'basic' && a.category !== 'rainbow')
    .sort((a, b) => {
      const ai = DISPLAY_ORDER.indexOf(a.category);
      const bi = DISPLAY_ORDER.indexOf(b.category);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  // Within each section, enabled categories float to top
  const sortEnabled = (list: typeof allNonBasic) => {
    const enabled = list.filter((a) => enabledCategories.includes(a.category));
    const disabled = list.filter((a) => !enabledCategories.includes(a.category));
    return [...enabled, ...disabled];
  };

  const primaryAllocations = sortEnabled(allNonBasic.filter((a) => PRIMARY_CATEGORIES.has(a.category)));
  const moreAllocations = sortEnabled(allNonBasic.filter((a) => !PRIMARY_CATEGORIES.has(a.category)));
  const hasEnabledMore = moreAllocations.some((a) => enabledCategories.includes(a.category));

  /** Render a single category row. */
  function renderCategoryRow(alloc: LandCategoryAllocation) {
    const isEnabled = enabledCategories.includes(alloc.category);
    const isTapland = TAPLAND_EXCEPTION_CATEGORIES.has(alloc.category);
    const isFetchable = FETCHABLE_CATEGORIES.has(alloc.category);
    const description = CYCLE_DESCRIPTIONS.get(alloc.category);
    const avgPrice = CYCLE_PRICES.get(alloc.category);
    const constraint = categoryConstraints?.get(alloc.category);
    const isFetchCategory = alloc.category === 'fetch-on' || alloc.category === 'fetch-off';

    return (
      <div
        key={alloc.category}
        className={`flex flex-col gap-0.5 p-2 rounded-lg border transition-colors ${
          isEnabled
            ? 'border-gray-700 bg-gray-800/50'
            : 'border-gray-800 bg-gray-900/30'
        }`}
      >
        {/* Top row: checkbox, label, badges */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={() => onToggle(alloc.category)}
            className="accent-orange-500 cursor-pointer shrink-0"
            aria-label={`Enable ${alloc.label}`}
          />
          <span className="flex-1 min-w-0">
            <span className={`text-xs font-medium leading-tight ${
              isEnabled ? 'text-gray-200' : 'text-gray-500'
            }`}>
              {alloc.label}
            </span>
            {(isTapland || isFetchable) && (
              <span className="flex gap-1 mt-0.5">
                {isTapland && (
                  <span className="inline-block px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide rounded bg-amber-900/60 text-amber-300 border border-amber-700/50">
                    tapped
                  </span>
                )}
                {isFetchable && (
                  <span className="inline-block px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide rounded bg-blue-900/60 text-blue-300 border border-blue-700/50">
                    fetchable
                  </span>
                )}
              </span>
            )}
          </span>
        </div>

        {/* Description + price */}
        {(description || avgPrice != null) && (
          <p className={`text-[10px] leading-tight ml-5 ${isEnabled ? 'text-gray-500' : 'text-gray-600'}`}>
            {description}
            {avgPrice != null && (
              <span className={`ml-1 ${isEnabled ? 'text-gray-400' : 'text-gray-600'}`}>
                ~${avgPrice < 1 ? avgPrice.toFixed(2) : avgPrice}/ea
              </span>
            )}
          </p>
        )}

        {/* Min/max constraint controls (only for enabled non-fetch categories) */}
        {isEnabled && !isFetchCategory && (onSetMin || onSetMax) && (
          <div className="flex items-center gap-2 ml-5 mt-0.5">
            {onSetMin && (
              <label className="flex items-center gap-1 text-[10px] text-gray-500">
                <span>min</span>
                <input
                  type="number"
                  min={0}
                  max={alloc.maxAvailable}
                  value={constraint?.min ?? ''}
                  placeholder="auto"
                  onChange={(e) => {
                    const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    onSetMin(alloc.category, isNaN(v as number) ? undefined : v);
                  }}
                  className="w-12 bg-gray-900 border border-gray-700 rounded px-1 py-0 text-[10px] text-gray-300 tabular-nums"
                />
              </label>
            )}
            {onSetMax && (
              <label className="flex items-center gap-1 text-[10px] text-gray-500">
                <span>max</span>
                <input
                  type="number"
                  min={0}
                  max={alloc.maxAvailable}
                  value={constraint?.max ?? ''}
                  placeholder="auto"
                  onChange={(e) => {
                    const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    onSetMax(alloc.category, isNaN(v as number) ? undefined : v);
                  }}
                  className="w-12 bg-gray-900 border border-gray-700 rounded px-1 py-0 text-[10px] text-gray-300 tabular-nums"
                />
              </label>
            )}
          </div>
        )}

        {/* Fetch max constraint (only for enabled fetch categories) */}
        {isEnabled && isFetchCategory && onSetMax && (
          <div className="flex items-center gap-2 ml-5 mt-0.5">
            <label className="flex items-center gap-1 text-[10px] text-gray-500">
              <span>max</span>
              <input
                type="number"
                min={0}
                max={alloc.maxAvailable}
                value={constraint?.max ?? ''}
                placeholder="all"
                onChange={(e) => {
                  const v = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                  onSetMax(alloc.category, isNaN(v as number) ? undefined : v);
                }}
                className="w-12 bg-gray-900 border border-gray-700 rounded px-1 py-0 text-[10px] text-gray-300 tabular-nums"
              />
            </label>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
        Land type distribution
      </p>

      {/* Two-column grid of primary land type cycles */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {primaryAllocations.map(renderCategoryRow)}
      </div>

      {/* "More" section — collapsed by default */}
      {moreAllocations.length > 0 && (
        <div className="border-t border-gray-700 pt-2">
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-300 transition-colors w-full"
          >
            <span className={`transition-transform ${showMore ? 'rotate-90' : ''}`}>▶</span>
            More land types ({moreAllocations.length})
            {hasEnabledMore && (
              <span className="text-orange-400 text-[10px]">• {moreAllocations.filter(a => enabledCategories.includes(a.category)).length} enabled</span>
            )}
          </button>
          {showMore && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-2">
              {moreAllocations.map(renderCategoryRow)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
